import { ZachmanLayer } from '../types';

export const championIdeaArchitecture: ZachmanLayer[] = [
  {
    id: 'l1-business',
    name: "Business Layer (Planner's View)",
    perspective: 'business',
    description: "Defines the high-level business strategy, goals, and scope for the Homomorphic ML Platform.",
    components: [
      {
        id: 'c1-what',
        name: 'What (Inventory)',
        description: 'The significant things the business deals with.',
        status: 'planned',
        what: 'Sensitive Customer Data (Healthcare, Finance), AI Models, Training Datasets, Intellectual Property.',
        how: 'Through secure data intake, privacy-preserving analytics, and model deployment.',
        where: 'Regulated industries, global markets, cross-border collaborations.',
        who: 'Business Executives, Data Scientists, Compliance Officers.',
        when: 'Aligned with strategic business cycles and product launch timelines.',
        why: 'To unlock AI in regulated markets while ensuring data privacy.',
      },
      {
        id: 'c1-how',
        name: 'How (Process)',
        description: 'The major business processes.',
        status: 'planned',
        what: 'Data is processed homomorphically.',
        how: 'Privacy-Preserving AI Training, Secure Inference, Collaborative Model Development.',
        where: 'In secure cloud environments and on-premise data centers.',
        who: 'ML Engineers, Data Owners.',
        when: 'Real-time for inference, scheduled for training.',
        why: 'To enable AI functionality without exposing raw data.',
      },
      {
        id: 'c1-where',
        name: 'Where (Distribution)',
        description: 'The geographic locations and network distribution of the business.',
        status: 'planned',
        what: 'Data and services are distributed globally.',
        how: 'Via a multi-cloud, geo-redundant network architecture.',
        where: 'Healthcare Systems (e.g., EPIC, Cerner), Financial Platforms, Government Systems.',
        who: 'Network Architects, Cloud Providers.',
        when: 'Phased rollout across regions, starting in North America and Europe.',
        why: 'To serve a global customer base and comply with data residency laws.',
      },
      {
        id: 'c1-who',
        name: 'Who (Responsibility)',
        description: 'The people and organizations involved.',
        status: 'planned',
        what: 'Roles and responsibilities for data governance and use.',
        how: 'Through a defined RACI matrix and access control policies.',
        where: 'Across business units, including legal, engineering, and product.',
        who: 'Data Scientists, Compliance Officers, Business Analysts, End-Users.',
        when: 'Ongoing throughout the platform lifecycle.',
        why: 'To ensure clear ownership and accountability for privacy and security.',
      },
      {
        id: 'c1-when',
        name: 'When (Timing)',
        description: 'The critical business cycles and event timings.',
        status: 'planned',
        what: 'Timelines for model training, deployment, and compliance audits.',
        how: 'Using agile development cycles and continuous monitoring.',
        where: 'Events are triggered by user actions or scheduled jobs.',
        who: 'Product Managers, Project Managers.',
        when: 'Real-time Inference, Scheduled Model Training, Quarterly Audits.',
        why: 'To ensure timely delivery of features and adherence to schedules.',
      },
      {
        id: 'c1-why',
        name: 'Why (Motivation)',
        description: 'The motivations and goals of the business.',
        status: 'planned',
        what: 'The core business drivers.',
        how: 'By creating a first-mover advantage and a strong IP portfolio.',
        where: 'The entire business strategy is driven by this motivation.',
        who: 'Company Leadership, Board of Directors, Investors.',
        when: 'This is the foundational, long-term motivation.',
        why: 'To unlock a $100B+ market, ensure unbreakable data privacy, gain significant competitive advantage, and become the industry standard.',
      },
    ],
  },
  {
    id: 'l2-application',
    name: "Application Layer (Owner's View)",
    perspective: 'application',
    description: 'Describes the system from the perspective of its users and owners, focusing on functionality.',
    components: [
      {
        id: 'c2-what',
        name: 'What (Data)',
        description: 'Data used and managed by the application.',
        status: 'planned',
        what: 'Application APIs, UI Components, Model Serving Endpoints, Encrypted Data Streams.',
        how: 'Managed via a service-oriented architecture.',
        where: 'Within the application backend and frontend.',
        who: 'ML Engineers, Application Developers.',
        when: 'During application runtime.',
        why: 'To provide the core functionality of the platform to users.',
      },
      {
        id: 'c2-how',
        name: 'How (Function)',
        description: 'The application-level functions and services.',
        status: 'planned',
        what: 'Core application logic.',
        how: 'Microservices for Encryption, Model Training, Inference Engine, and Platform Dashboard.',
        where: 'Executed within the Kubernetes cluster.',
        who: 'Backend and Frontend Engineers.',
        when: 'On-demand based on user requests.',
        why: 'To deliver a scalable, resilient, and user-friendly platform.',
      },
      {
        id: 'c2-where',
        name: 'Where (Network)',
        description: 'How the application is deployed across the network.',
        status: 'planned',
        what: 'Deployment topology.',
        how: 'As containerized services on a cloud-native platform (AWS/GCP).',
        where: 'Across multiple availability zones within a virtual private cloud (VPC).',
        who: 'DevOps Engineers, SREs.',
        when: 'Managed by Kubernetes for high availability.',
        why: 'To ensure resilience, scalability, and security of the application.',
      },
       {
        id: 'c2-who',
        name: 'Who (Interfaces)',
        description: 'The user roles and system interfaces.',
        status: 'planned',
        what: 'User roles and permissions.',
        how: 'Role-Based Access Control (RBAC) integrated with OAuth 2.0.',
        where: 'User-facing web dashboard and programmatic APIs.',
        who: 'Platform Developers, End-Users (Data Scientists, Analysts).',
        when: 'Authentication and authorization checks on every request.',
        why: 'To provide secure, tailored access to platform features for different user types.',
      },
      {
        id: 'c2-when',
        name: 'When (Events)',
        description: 'Application-level events and workflows.',
        status: 'planned',
        what: 'Key application events.',
        how: 'CI/CD Pipelines for Deployment, Automated Scaling Events, API Request/Response Lifecycle.',
        where: 'Orchestrated by tools like GitHub Actions and Kubernetes Horizontal Pod Autoscaler.',
        who: 'DevOps team, Engineering Leads.',
        when: 'Triggered by code commits, performance metrics, or user traffic.',
        why: 'To automate the software delivery lifecycle and ensure optimal performance.',
      },
      {
        id: 'c2-why',
        name: 'Why (Goal)',
        description: 'The purpose of the application.',
        status: 'planned',
        what: 'The application mission.',
        how: 'By abstracting the complexity of homomorphic encryption.',
        where: 'In all aspects of the application design.',
        who: 'Product Managers, System Architects.',
        when: 'This is the guiding principle for development.',
        why: 'To provide a usable, scalable platform for building privacy-preserving AI applications.',
      }
    ],
  },
  {
    id: 'l3-data',
    name: "Data Layer (Designer's View)",
    perspective: 'data',
    description: 'The logical data model and architecture, independent of physical storage.',
    components: [
       {
        id: 'c3-what',
        name: 'What (Logical Model)',
        description: 'The logical entities and relationships.',
        status: 'planned',
        what: 'Encrypted Data Stores, Homomorphic Query Capabilities, Logical Data Schemas for Health/Finance, ML Model Artifacts.',
        how: 'Defined using logical data modeling techniques.',
        where: 'In the system design documentation.',
        who: 'Data Architects, Cryptography Engineers.',
        when: 'Defined before physical database implementation.',
        why: 'To ensure data is structured for privacy, integrity, and computational efficiency.',
      },
       {
        id: 'c3-how',
        name: 'How (Data Flow)',
        description: 'How data moves and transforms through the system.',
        status: 'planned',
        what: 'The data lifecycle.',
        how: 'Secure Data Transport Protocols, Homomorphic Operations (addition/multiplication on ciphertexts).',
        where: 'Within secure, isolated data processing pipelines.',
        who: 'Data Engineers, Cryptographers.',
        when: 'When data is ingested, processed, or queried.',
        why: 'To ensure data is mathematically guaranteed to be private at all stages.',
      },
      {
        id: 'c3-where',
        name: 'Where (Distribution)',
        description: 'The logical distribution of data.',
        status: 'planned',
        what: 'Data storage strategy.',
        how: 'Using distributed databases and encrypted data lakes.',
        where: 'Logically separated data stores for different tenants and environments.',
        who: 'Data Architects, DBAs.',
        when: 'Data is partitioned and distributed based on access patterns.',
        why: 'To provide scalability and data isolation.',
      },
      {
        id: 'c3-who',
        name: 'Who (Ownership)',
        description: 'Data ownership and stewardship.',
        status: 'planned',
        what: 'Data governance roles.',
        how: 'Data stewards are assigned to each major data domain.',
        where: 'Policies are managed in a central data governance tool.',
        who: 'Data Architects, Data Stewards, Compliance Officers.',
        when: 'Policies are reviewed and enforced continuously.',
        why: 'To establish clear accountability for data quality and security.',
      },
      {
        id: 'c3-when',
        name: 'When (Lifecycle)',
        description: 'The data lifecycle from creation to archival.',
        status: 'planned',
        what: 'Data retention policies.',
        how: 'Automated policies for data encryption at rest, in transit, and in use (homomorphic).',
        where: 'Applied across all data storage and processing systems.',
        who: 'Data Governance team.',
        when: 'Data is encrypted upon creation and remains so for its entire lifecycle.',
        why: 'To enforce privacy and comply with regulations like GDPR ("right to be forgotten").',
      },
      {
        id: 'c3-why',
        name: 'Why (Integrity)',
        description: 'The principles ensuring data quality and integrity.',
        status: 'planned',
        what: 'Data integrity constraints.',
        how: 'Through cryptographic commitments and zero-knowledge proofs.',
        where: 'Validated during data ingestion and processing.',
        who: 'Cryptography Engineers, Data Engineers.',
        when: 'At every data transformation step.',
        why: 'To trust the results of computations on encrypted data.',
      }
    ]
  },
  {
    id: 'l4-technology',
    name: "Technology Layer (Builder's View)",
    perspective: 'technology',
    description: 'The technologies and tools used to build and deploy the system.',
    components: [
      {
        id: 'c4-what',
        name: 'What (Tech Stack)',
        description: 'The specific hardware and software components.',
        status: 'planned',
        what: 'React/TypeScript, Rust, Kubernetes, Docker, PostgreSQL, AWS/GCP.',
        how: 'These technologies are integrated to form the platform.',
        where: 'Deployed on public cloud infrastructure.',
        who: 'Backend Developers, Frontend Developers, SREs.',
        when: 'Chosen during the initial design phase.',
        why: 'To use best-in-class technologies for performance, security, and developer productivity.',
      },
       {
        id: 'c4-how',
        name: 'How (Implementation)',
        description: 'How the technology is used to implement the system.',
        status: 'planned',
        what: 'The implementation patterns.',
        how: 'Rust microservices for HE operations, React for privacy-focused UI, Kubernetes for orchestration.',
        where: 'Code is stored in Git repositories and built via CI/CD.',
        who: 'Full-stack development teams.',
        when: 'During development sprints.',
        why: 'To translate the logical design into working code.',
      },
      {
        id: 'c4-where',
        name: 'Where (Environment)',
        description: 'The deployment environments.',
        status: 'planned',
        what: 'The hosting environment.',
        how: 'Using specific AWS/GCP services (EKS, GKE, Nitro Enclaves) within dedicated VPCs.',
        where: 'Separate environments for Development, Testing, Staging, and Production.',
        who: 'DevOps and Infrastructure teams.',
        when: 'Infrastructure is provisioned on-demand.',
        why: 'To ensure a stable, secure, and isolated environment for all stages of development.',
      },
      {
        id: 'c4-who',
        name: 'Who (Expertise)',
        description: 'The skills and expertise required.',
        status: 'planned',
        what: 'Required technical skills.',
        how: 'By hiring and training engineers with these skills.',
        where: 'Within the engineering organization.',
        who: 'Cryptography Engineers, Rust Developers, Kubernetes Experts, ML Engineers.',
        when: 'As the project scales.',
        why: 'To ensure the team has the capability to build and maintain the platform.',
      },
       {
        id: 'c4-when',
        name: 'When (Automation)',
        description: 'The timing of technology-related processes.',
        status: 'planned',
        what: 'Automation and deployment schedules.',
        how: 'Just-in-time compilation of HE circuits, container build/deployment via CI/CD.',
        where: 'Automated pipelines trigger on code commits.',
        who: 'DevOps Engineers.',
        when: 'Continuously, throughout the development lifecycle.',
        why: 'To enable rapid, reliable, and repeatable deployments.',
      },
      {
        id: 'c4-why',
        name: 'Why (Rationale)',
        description: 'The justification for technology choices.',
        status: 'planned',
        what: 'The reasons for the tech stack.',
        how: 'Rust for performance and safety, Kubernetes for scalability, React for modern UI.',
        where: 'Documented in architectural decision records (ADRs).',
        who: 'Architects, Tech Leads.',
        when: 'During the system design phase.',
        why: 'To make informed, defensible technology choices that align with project goals.',
      },
    ]
  },
  {
    id: 'l5-infrastructure',
    name: "Infrastructure Layer (Implementer's View)",
    perspective: 'infrastructure',
    description: 'The physical infrastructure, including servers, networks, and data centers.',
    components: [
      {
        id: 'c5-what',
        name: 'What (Configuration)',
        description: 'The detailed configuration of infrastructure components.',
        status: 'planned',
        what: 'Server Configurations, Network Routing Tables, Firewall Rules, Hardware Security Modules (HSMs).',
        how: 'Defined as code using tools like Terraform and Ansible.',
        where: 'Applied to cloud resources and physical hardware.',
        who: 'Infrastructure Engineers, Security Operations.',
        when: 'When new infrastructure is provisioned or updated.',
        why: 'To ensure consistent, repeatable, and secure infrastructure setup.',
      },
      {
        id: 'c5-how',
        name: 'How (Provisioning)',
        description: 'The process of setting up the physical infrastructure.',
        status: 'planned',
        what: 'Infrastructure as Code (IaC).',
        how: 'Terraform scripts for provisioning, Ansible playbooks for configuration.',
        where: 'Executed via CI/CD pipelines.',
        who: 'Infrastructure and DevOps teams.',
        when: 'Automatically, in response to system requirements.',
        why: 'To automate the creation and management of infrastructure.',
      },
      {
        id: 'c5-where',
        name: 'Where (Location)',
        description: 'The physical location of the infrastructure.',
        status: 'planned',
        what: 'Physical and cloud locations.',
        how: 'Deployed across multiple geographic regions and availability zones for redundancy.',
        where: 'Physical Data Centers (for HSMs), AWS/GCP Regions (e.g., us-east-1, eu-west-2).',
        who: 'Cloud providers, Data Center operations.',
        when: 'Determined by business requirements for latency and data sovereignty.',
        why: 'To provide high availability, disaster recovery, and global reach.',
      },
      {
        id: 'c5-who',
        name: 'Who (Operations)',
        description: 'The teams responsible for infrastructure operations.',
        status: 'planned',
        what: 'The operations team structure.',
        how: 'Through a 24/7 on-call rotation and SRE practices.',
        where: 'A dedicated Network Operations Center (NOC) and SRE team.',
        who: 'Infrastructure Engineers, Network Engineers, Security Operations (SecOps).',
        when: 'Ongoing monitoring and maintenance.',
        why: 'To ensure the underlying infrastructure is reliable, performant, and secure.',
      },
      {
        id: 'c5-when',
        name: 'When (Events)',
        description: 'Infrastructure-level events and timings.',
        status: 'planned',
        what: 'Key infrastructure events.',
        how: 'Server boot times, network latency monitoring, automated recovery processes.',
        where: 'Monitored by tools like Prometheus and Grafana.',
        who: 'SRE team.',
        when: 'Continuously monitored with automated alerts for anomalies.',
        why: 'To proactively manage infrastructure health and performance.',
      },
      {
        id: 'c5-why',
        name: 'Why (Goal)',
        description: 'The purpose of the infrastructure configuration.',
        status: 'planned',
        what: 'The infrastructure design goals.',
        how: 'By adhering to principles of high availability, security, and cost-optimization.',
        where: 'Reflected in all infrastructure choices.',
        who: 'Head of Infrastructure, CTO.',
        when: 'This is the guiding philosophy for all infrastructure work.',
        why: 'To build a robust, secure, and performant foundation that underpins the entire platform.',
      },
    ]
  }
]; 